import {
  Arrow2,
  Content2,
  Portal,
  Provider,
  Root3,
  Trigger
} from "../../../chunk-JDOTBHDX.mjs";
import "../../../chunk-UFV3XC2K.mjs";
import "../../../chunk-VQI4KMJ3.mjs";
import "../../../chunk-MTQX4T3Q.mjs";
import "../../../chunk-XFG5THGD.mjs";
import "../../../chunk-RRWBFD4V.mjs";
import "../../../chunk-HFXIPTW6.mjs";
import "../../../chunk-OPCIACWD.mjs";
import "../../../chunk-GYRZVAMU.mjs";
import "../../../chunk-QOCR4XUU.mjs";
import "../../../chunk-D6QHF2UO.mjs";
import "../../../chunk-7TMHQ6XO.mjs";
import "../../../chunk-BQ72SK4G.mjs";
import "../../../chunk-FBWCVJGC.mjs";
import "../../../chunk-CD4FQC4X.mjs";
import "../../../chunk-D57E6H3M.mjs";

// src/components/primitive/CopyButton/CopyButton.tsx
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { MdContentCopy } from "react-icons/md";
import { useShadowPortalContainer } from "../../../hooks/useShadowPortalContainer.mjs";
import { useComponentsTranslation } from "../../../i18n/useComponentsTranslation.mjs";
import { Button } from "../Button/Button.mjs";
import { DrawnCheck } from "../DrawnCheck/DrawnCheck.mjs";
import { keyboardFocusOnly, useTooltipPointer, useTooltipShown } from "../Tooltip/tooltipTrigger.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var cls = (...parts) => parts.filter(Boolean).join(" ");
var FEEDBACK_MS = 2e3;
function CopyButton({ ref, value, label, onCopy, size = "xs", variant = "bordered", className, onClick, "aria-label": ariaLabel, ...props }) {
  const { t } = useComponentsTranslation();
  const [state, setState] = useState("idle");
  const [hovered, setHovered] = useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);
  const [tooltipContainer, handleTooltipTrigger] = useShadowPortalContainer();
  const [tooltipTrigger, setTooltipTrigger] = useState(null);
  const tooltipTriggerRef = useCallback((node) => {
    handleTooltipTrigger(node);
    setTooltipTrigger(node);
  }, [handleTooltipTrigger]);
  const closeOnPress = useTooltipPointer(tooltipTrigger, setHovered);
  const timer = useRef(void 0);
  const frame = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => () => {
    clearTimeout(timer.current);
    cancelAnimationFrame(frame.current);
  }, []);
  const settle = (next) => {
    clearTimeout(timer.current);
    cancelAnimationFrame(frame.current);
    setFeedbackDismissed(false);
    if (stateRef.current === next) {
      setState("idle");
      frame.current = requestAnimationFrame(() => setState(next));
    } else {
      setState(next);
    }
    timer.current = setTimeout(setState, FEEDBACK_MS, "idle");
  };
  const copy = async (event) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    try {
      await navigator.clipboard.writeText(value);
      settle("copied");
      onCopy?.(value);
    } catch {
      settle("failed");
    }
  };
  const status = state === "copied" ? t("copyButton.copied") : state === "failed" ? t("copyButton.failed") : "";
  const iconOnly = !label;
  const name = iconOnly ? ariaLabel ?? t("copyButton.copy") : ariaLabel;
  const tip = state === "idle" ? iconOnly ? name : void 0 : status;
  const tipOpen = !!tip && (state === "idle" ? hovered : !feedbackDismissed);
  const { swap, popupRef } = useTooltipShown(tipOpen);
  const button = /* @__PURE__ */ jsxs(
    Button,
    {
      ref,
      size,
      variant,
      color: "neutral",
      iconOnly,
      "aria-label": name,
      className: cls("buyscout-copy-button", state !== "idle" && `buyscout-copy-button--${state}`, className),
      onClick: copy,
      ...props,
      children: [
        /* @__PURE__ */ jsxs("span", { className: "buyscout-copy-button__glyphs", "aria-hidden": true, children: [
          /* @__PURE__ */ jsx(MdContentCopy, { "data-shown": state !== "copied" || void 0 }),
          /* @__PURE__ */ jsx(DrawnCheck, { drawn: state === "copied", "data-shown": state === "copied" || void 0 })
        ] }),
        !iconOnly && /* @__PURE__ */ jsx("span", { children: label })
      ]
    }
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Provider, { delayDuration: 0, children: /* @__PURE__ */ jsxs(Root3, { open: tipOpen, onOpenChange: setHovered, children: [
      /* @__PURE__ */ jsx(Trigger, { asChild: true, ref: tooltipTriggerRef, onFocus: keyboardFocusOnly, onPointerDown: closeOnPress, "aria-describedby": props["aria-describedby"], children: button }),
      /* @__PURE__ */ jsx(Portal, { container: tooltipContainer, children: /* @__PURE__ */ jsxs(
        Content2,
        {
          ref: popupRef,
          "data-swap": swap || void 0,
          "aria-hidden": true,
          side: "top",
          sideOffset: 6,
          collisionPadding: 8,
          className: "buyscout-tooltip-popup",
          onEscapeKeyDown: () => setFeedbackDismissed(true),
          children: [
            tip,
            /* @__PURE__ */ jsx(Arrow2, { className: "buyscout-tooltip-popup__arrow", width: 8, height: 4 })
          ]
        }
      ) })
    ] }) }),
    /* @__PURE__ */ jsx("span", { role: "status", className: "buyscout-copy-button__status", children: status })
  ] });
}
CopyButton.displayName = "CopyButton";
function CopyField({ ref, value, label, fullWidth = false, wrap = false, onCopy, className, ...props }) {
  const valueId = useId();
  return /* @__PURE__ */ jsxs("div", { ref, className: cls("buyscout-copy-field", fullWidth && "buyscout-copy-field--full-width", wrap && "buyscout-copy-field--wrap", className), ...props, children: [
    /* @__PURE__ */ jsx("span", { id: valueId, className: "buyscout-copy-field__value", title: wrap ? void 0 : value, children: value }),
    /* @__PURE__ */ jsx(CopyButton, { value, label, onCopy, "aria-describedby": valueId })
  ] });
}
CopyField.displayName = "CopyField";
export {
  CopyButton,
  CopyField
};
//# sourceMappingURL=CopyButton.mjs.map